import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context';
import { useSocketsContext } from 'src/providers/SocketsProvider/Sockets.context';
import { sentry } from 'src/sentry/sentry.singleton';
import { WalletService } from './services/Wallet.service';
import { useWalletFlag } from './hooks/useWalletFlag';
import { useAnyWapiComposerOpen } from './hooks/useWapiComposerPresence';
import { WalletContext } from './Wallet.context';
import { esEstampaFresca, FREE_TIER_READY_MAX_AGE_MS } from './Wallet.freeTier';
import { currentWalletMonthKey } from './Wallet.date';
import { CHARGED_STATUS, effectsFor, PENDING_STATUS } from './Wallet.outcome';
import {
  SETTLEMENT_WAIT_SCHEDULE_MS,
  classifySettlementFailure,
  hasSettlementBalanceIncreased,
} from './Wallet.settlement';
import type {
  tFreeTierCounter,
  tFreeTierCounterStatus,
  tTopUpOutcome,
  tWalletBalance,
  tWalletChargeOutcome,
  tWalletSettlement,
  tWalletStatus,
} from './Wallet.types';

/**
 * Wallet balance for the whole application (RET-1763).
 *
 * A provider rather than a per-component hook because the balance is not a
 * screen: the card renders in 3 places at once (RET-1689), campaigns and the
 * 1-to-1 chat read it for estimated cost (RET-1694), the low-balance banner
 * watches it (RET-1695), and from R2/R3 an empty wallet blocks sending. That
 * is application-wide state, the same class as workspace or subscription.
 *
 * One instance means exactly one request no matter how many surfaces mount,
 * and one value they all read — a refetch after a top-up updates every
 * surface at once.
 *
 * Mounts below FeatureFlagsProvider (needs the flag), WorkspaceProvider (needs
 * the workspace) and SocketsProvider (needs workspace.updated).
 */

/**
 * Every state carries the workspace it belongs to. Nothing is served unless
 * that stamp matches the workspace on screen right now (review finding #1).
 *
 * The bug this replaces: the effect set a loading flag but left the previous
 * balance in state, so workspace A's money kept flowing out of context for the
 * entire duration of workspace B's request. Clearing inside the effect would
 * still leave the gap between the render that changes `workspaceId` and the
 * effect that runs after it. Stamping closes the gap by construction — the
 * moment `workspaceId` changes, the stamp stops matching and the value is
 * simply not served.
 */
type tWalletState =
  | { kind: 'idle' }
  | { kind: 'loading'; workspaceId: string }
  | {
      kind: 'ready';
      workspaceId: string;
      balance: tWalletBalance;
      /**
       * RET-2327 (C-1): el `Date.now()` tomado en `performFetch` justo antes
       * de emitir la lectura que escribió este `ready`. Viaja con el MISMO
       * `workspaceId` que el saldo, por la misma razón de estampa — nunca se
       * sirve la marca de una lectura de otro workspace.
       */
      readStartedAt: number;
    }
  | { kind: 'error'; workspaceId: string; error: unknown };

/**
 * El contador de la franja de Meta (RET-2283), con la MISMA disciplina de
 * estampa que `tWalletState`: nada se sirve si no está estampado con el
 * workspace en pantalla.
 *
 * Sin forma `error` propia — a diferencia del saldo, nada en esta pantalla
 * distingue "no se pudo leer" de "la cohorte está apagada" o "todavía está
 * cargando": las tres son la MISMA respuesta para quien consume esto (el
 * sello calla y dice "Con costo"), así que las tres caen a `idle`. Añadir una
 * forma que nadie necesita distinguir sería estado que nadie lee.
 */
/**
 * RET-2319 · CUATRO estados, no tres, porque `idle` mezclaba dos cosas que
 * merecen respuestas OPUESTAS en el sello:
 *
 *   - `idle`    nadie preguntó todavía (arranque, o la compuerta cerrada antes
 *               de la primera lectura). Transitorio ⇒ el sello CALLA.
 *   - `loading` la respuesta viene en camino. Transitorio ⇒ el sello CALLA.
 *   - `ready`   hay contador, con `readAt` para poder juzgar su VEJEZ.
 *   - `failed`  se preguntó y la lectura cayó. DURABLE ⇒ el sello dice «Con
 *               costo», porque callar para siempre esconde un costo real.
 *
 * La vuelta 1 de este ticket mapeaba «nunca se preguntó» y «se preguntó y
 * falló» al mismo `idle`, y de ahí los dos agujeros que encontró la revisión:
 * el «Con costo» del primer frame comprometido y la imposibilidad de distinguir
 * una espera de una incógnita durable.
 */
type tFreeTierState =
  | { kind: 'idle' }
  | { kind: 'loading'; workspaceId: string }
  | {
      kind: 'ready';
      workspaceId: string;
      counter: tFreeTierCounter;
      readAt: number;
    }
  | { kind: 'failed'; workspaceId: string };

/**
 * El ÚNICO predicado de frescura DE ESTE ARCHIVO. Vivía inline en tres sitios
 * con la misma constante, y así es como un `<` se vuelve `<=` en uno solo (minor
 * de la revisión arquitectónica). Es un type guard para que el llamador no tenga
 * que re-estrechar el estado con un cast.
 *
 * RET-2319 · vuelta 3: el término de edad se delega a `esEstampaFresca`, que es
 * el dueño único de la cota (`Wallet.freeTier.ts`). El sello la juzga con ESE
 * mismo predicado y su propio reloj, porque hay un frame que este archivo no
 * alcanza; si la cota viviera dos veces, una podría moverse sin la otra.
 */
function esLecturaFresca(
  estado: tFreeTierState,
  workspaceId: string,
): estado is Extract<tFreeTierState, { kind: 'ready' }> {
  return (
    estado.kind === 'ready' &&
    estado.workspaceId === workspaceId &&
    esEstampaFresca(estado.readAt, Date.now())
  );
}

/**
 * El estado de la espera acotada, para adentro (RET-1925).
 *
 * Estampado con el workspace por la misma razón que `tWalletState`: la espera
 * muere con el workspace que la arrancó, y una respuesta o un render que llegue
 * después no debe pintar el estado intermedio en la pantalla de otro.
 *
 * NO guarda el saldo contra el que se rindió, y esa ausencia es una decisión de
 * la revisión de RET-1925. Lo guardaba, para apagarse solo cuando el saldo
 * subiera. Con la narrativa de la tarjeta fuera del alcance, el único lector de
 * este estado es el CANDADO del modal, y ahí apagarse solo es la dirección
 * equivocada: un abono ajeno —una cortesía, un reembolso, la recarga
 * automática— apagaba el candado y dejaba pasar la segunda recarga sin
 * fricción. Persistir es fallar cerrado.
 */
type tSettlementState =
  | { kind: 'idle' }
  | { kind: 'settling'; workspaceId: string; amountMicros: string | null }
  | { kind: 'exhausted'; workspaceId: string; amountMicros: string | null };

/**
 * Outcome of ONE balance read, for a caller that has to tell three cases
 * apart (RET-1870).
 *
 * `refetchBalance` narrows this back to `tWalletBalance | null`, so the
 * published context contract does not change. The settlement wait needs the
 * wider answer: a discarded read is not a failure — a newer read committed
 * first, so the reading it wanted may already be on screen — and a failure
 * has to be classified as terminal or transient with the error in hand.
 *
 * Rejected: leaving the return type alone and letting the wait call
 * `WalletService.getBalance` itself so it could catch its own error. That
 * needs a second copy of the commit path — issue number, workspace stamp,
 * flicker guard — and two copies of "how the balance gets written" is the
 * thing to delete, not the thing to add.
 */
type tBalanceReadResult =
  | { kind: 'committed'; balance: tWalletBalance }
  | { kind: 'discarded' }
  | { kind: 'failed'; error: unknown };

/**
 * `signal` cancels one caller's read, unchanged.
 *
 * `preserveVisibleBalanceOnFailure` answers one question: may this read degrade
 * what the customer can already see? Only the settlement wait says yes, and
 * only while there IS a good reading on screen to protect — saying yes with
 * nothing on screen suppresses the error card and its Retry button, leaving a
 * spinner with no request behind it (`loading` has no retry affordance;
 * `BalanceCard` renders Retry on `error` only). Every other caller omits it and
 * keeps today's fail-closed behaviour exactly.
 */
type tBalanceFetchOptions = {
  signal?: { cancelled: boolean };
  preserveVisibleBalanceOnFailure?: boolean;
};

const DISCARDED: tBalanceReadResult = { kind: 'discarded' };

/**
 * Qué se publica del estado de la espera (RET-1925).
 *
 * UN filtro, la ESTAMPA: una espera arrancada en un workspace no puede decidir
 * nada en otro. Es el mismo defecto que el memo ya filtraba para el dinero.
 *
 * Y NADA MÁS. Hubo aquí una derivación que apagaba el `exhausted` cuando el
 * saldo en pantalla superaba el que la espera dejó sin acreditar, y la revisión
 * la retiró por dos razones medidas. Apagaba con CUALQUIER aumento, así que un
 * abono ajeno —una cortesía, un reembolso, la recarga automática disparándose
 * sola— lo satisfacía. Y como se recalculaba en cada render sobre un estado que
 * vive para siempre, el consumo posterior podía hacerlo REAPARECER, u —ocupando
 * el aumento con débitos— dejarlo pegado sin poder apagarse nunca.
 *
 * El único lector de esto es el candado de la segunda recarga, y para él
 * persistir es fallar cerrado: de más, pide una confirmación que sobra; de
 * menos, deja pasar un segundo cobro real. El estado muere con su workspace o
 * lo reemplaza el siguiente cobro, y no antes.
 */
function settlementForScreen(
  settlement: tSettlementState,
  workspaceId: string,
): tWalletSettlement {
  if (settlement.kind === 'idle') return IDLE_SETTLEMENT;
  if (settlement.workspaceId !== workspaceId) return IDLE_SETTLEMENT;

  return { kind: settlement.kind, amountMicros: settlement.amountMicros };
}

/**
 * Una sola instancia, porque el memo la devuelve en la mayoría de los renders y
 * un objeto nuevo cada vez rompería cualquier comparación por referencia que un
 * consumidor haga en sus dependencias.
 */
const IDLE_SETTLEMENT: tWalletSettlement = { kind: 'idle' };

export default function WalletProvider(props: { children: ReactNode }) {
  const { currentWorkspace } = useWorkspaceContext();
  const workspaceId = currentWorkspace?.id ?? '';
  const { isWalletEnabled, isLoadingWalletFlag } = useWalletFlag();
  const { workspaceUpdatedEvent, walletBalanceUpdatedEvent } =
    useSocketsContext();

  /**
   * Mirrors the workspace on screen for `performFetch`'s write guard below
   * (RET-1870). Assigned in the render body, not an effect: React always
   * finishes running this function — this line included — and commits before
   * ANY effect for that render fires, so the mount effect that fetches for a
   * newly-current workspace can never see a value older than the one that
   * belongs to it. An effect-based mirror would instead lag by one flush and
   * make the entry `loading` write below depend on which of two effects React
   * happens to run first.
   *
   * Safe only because no workspace switch here is wrapped in `startTransition`
   * (verified: none exists in this app) — a discarded transition render would
   * leave this ref ahead of the committed state and misfire the guard, and if
   * that ever changes this must become a `useLayoutEffect` instead.
   */
  const currentWorkspaceIdRef = useRef(workspaceId);
  currentWorkspaceIdRef.current = workspaceId;

  const [state, setState] = useState<tWalletState>({ kind: 'idle' });
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  /**
   * Contador de invalidación de la configuración de la recarga automática
   * (RET-1928). `useAutoTopUp` lo lee como dependencia de su efecto.
   *
   * Un CONTADOR y no una función imperativa, porque quien tiene que releer vive
   * DENTRO de `ConnectedBalanceCard`, que se monta en tres lugares — un nivel
   * por debajo de quien es dueño del cierre del modal. Ésa es la razón por la
   * que el `refetch` que el hook exponía nunca tuvo un llamador: cablearlo
   * exigía cruzar una frontera de componente en cada sitio de montaje, y bastaba
   * olvidar uno. Con el token, la superficie invalida sin saber quién escucha.
   */
  const [autoTopUpToken, setAutoTopUpToken] = useState(0);
  const [settlement, setSettlement] = useState<tSettlementState>({
    kind: 'idle',
  });
  const [freeTierState, setFreeTierState] = useState<tFreeTierState>({
    kind: 'idle',
  });
  const hasOpenWapiComposer = useAnyWapiComposerOpen();
  /**
   * RET-2283 (arreglo C del orquestador, tras la vuelta 1): qué workspaces ya
   * reportaron una lectura caída del contador EN ESTA SESIÓN del provider.
   *
   * Antes de este refresco, una lectura caída se reportaba una vez por visita
   * al tab Saldo. Con el poll de 60 s corriendo mientras haya un chat WAPI
   * abierto, reportar CADA fallo pasa de eso a "una vez por minuto por
   * pestaña" — multiplicado por la cohorte (879 workspaces hoy) es el mismo
   * ruido que ya aprendió a ignorar Sentry (el propio docblock de
   * `reportUnexplainedTierCounter`, Wallet.service.ts, hace este argumento
   * para el caso hermano). Se reporta sólo la PRIMERA falla de cada
   * workspace; una lectura que SÍ llega borra su marca, así que una
   * recuperación seguida de una caída nueva vuelve a avisar.
   */
  const reportedFreeTierReadFailures = useRef<Set<string>>(new Set());

  /**
   * Write ordering for the SAME workspace (RET-1870).
   *
   * `setState` is a last-write-wins door, and the two guards this file already
   * has do not order these writes: the workspace stamp orders responses ACROSS
   * workspaces, and `signal.cancelled` cancels per caller. Neither covers two
   * callers racing inside one workspace — the effect's fetch and an imperative
   * `refetchBalance` are exactly that pair, and the effect's signal stays live
   * because nothing changed its dependencies.
   *
   * Without ordering, a slower response that was issued EARLIER overwrites a
   * newer one that already committed. On a top-up that means showing the
   * credited balance and then pulling it back to the pre-credit number, where
   * it stays. Showing the truth and retracting it is worse than never showing
   * it.
   *
   * Each fetch takes a monotonic issue number and may only commit while no
   * later-issued fetch has committed. An older response that is not yet
   * superseded still commits, so this orders writes without dropping readings.
   *
   * Only a SUCCESS raises the floor. An error is not a reading, so it must not
   * shadow one — see the catch branch for why that asymmetry is the fix and not
   * an oversight.
   *
   * Two things this does NOT promise. It does not order the `loading` write
   * below, which is exempt because it runs synchronously with the increment and
   * so can never follow a later issue's commit; its hazard is the workspace it
   * names, not when it runs. And it does not promise the balance never goes
   * backwards: ordering is by request ISSUE time, not by the moment
   * wallet-service read the ledger, so two reads issued milliseconds apart can
   * still be served in the opposite order by the backend. This rules out the
   * front end causing a regression, nothing more.
   */
  const issueCounterRef = useRef(0);
  const lastCommittedIssueRef = useRef(0);

  /**
   * The `amountMicros` of the last committed reading, stamped with the
   * workspace it belongs to. Written in the same breath as the floor above,
   * so the two cannot drift.
   *
   * The settlement wait reads this instead of `state`, for two reasons. It
   * needs its baseline synchronously, before its first await, and re-reading
   * shared state mid-await is the trap in
   * `docs/solutions/logic-errors/websocket-race-overwrites-guard-state-during-await.md`
   * — the socket refetch is exactly the parallel writer described there. And
   * when its own read is discarded because a parallel one committed first, it
   * needs to know what actually landed on screen.
   *
   * Cleared wherever what is on screen stops being a reading: the `idle` write
   * and the `error` write both do it. Left uncleared past either one it
   * survives into a later wait as a baseline nothing refreshed — a logout and
   * back with a failed mount read, or simply a failed read — and that wait sees
   * a change on its very first read and stops one read in. That is the original
   * bug in miniature.
   *
   * NOT part of the ordering scheme. The floor above is deliberately never
   * cleared — an earlier review pass left the terminal `idle` write out of
   * ordering on purpose — and invalidating a cache is a different job from
   * ordering writes.
   */
  const committedBalanceRef = useRef<{
    workspaceId: string;
    amountMicros: string;
  } | null>(null);

  /**
   * True when a later-issued fetch already committed, so this response is stale
   * and must not be written.
   *
   * The floor is global, not per workspace, and `isDiscardedWorkspace` below is
   * what makes that safe: it returns before the floor assignment, so no
   * workspace other than the one on screen can ever raise the bar. Every
   * workspace switch issues a fresh read whose number is above the floor, so
   * the legitimate fetch always clears it.
   *
   * Global also makes the floor monotonic for free: only a read that already
   * satisfies `issue >= floor` reaches the assignment, so it can never move
   * down. A per-workspace floor loses that, and with it the A -> B -> A case,
   * where a read issued on A before the trip lands with A on screen again and
   * `isDiscardedWorkspace` has no grounds to reject it.
   */
  const isSuperseded = useCallback(
    (issue: number): boolean => issue < lastCommittedIssueRef.current,
    [],
  );

  /**
   * True when a fetch's target is no longer the workspace on screen (RET-1870).
   *
   * `refetchBalance` closes over the workspace of the render that created it.
   * A caller that captured it before a workspace switch and invokes it after
   * — a handler in a promise chain, a timer, an async submit awaiting a
   * network call — issues a fetch for a workspace nobody is looking at
   * anymore. The stamp check in the `value` memo below stops that fetch's
   * WRONG NUMBER from being read, but a memo only runs at read time: it
   * cannot undo a `setState` that already replaced the current workspace's
   * correct state with this call's record. This guard stops the write itself,
   * at all three sites `performFetch` writes state.
   *
   * Distinct from `isSuperseded`: that one orders callers by issue number.
   * This one rejects a caller whose target workspace isn't even the one on
   * screen, regardless of issue order — a stale-workspace fetch can be the
   * very first one issued and still must never write.
   */
  const isDiscardedWorkspace = useCallback(
    (targetWorkspaceId: string): boolean =>
      targetWorkspaceId !== currentWorkspaceIdRef.current,
    [],
  );

  // workspace-service emits workspace.updated whenever a billing event mutates
  // the workspace — the same signal FeatureFlagsProvider already listens to.
  workspaceUpdatedEvent.useSubscription(() => {
    setRefetchTrigger((previous) => previous + 1);
  });

  // wallet-service signals "this workspace's balance changed" after crediting
  // the ledger (ADR-013, RET-1870). The signal carries no amounts: it only
  // invalidates, and this refetch is what repaints — the pull stays the source.
  // The socket room already scopes delivery to the current workspace; the guard
  // is defense against a stray or late signal after a workspace switch.
  walletBalanceUpdatedEvent.useSubscription((signal) => {
    if (signal?.workspace_id && signal.workspace_id !== workspaceId) return;
    setRefetchTrigger((previous) => previous + 1);
  });

  /**
   * Single fetch implementation shared by the effect and the imperative
   * `refetchBalance`, mirroring FeatureFlagsProvider's performFetch. It
   * resolves with the committed value so a money-moving caller can await the
   * new balance before closing a modal (review finding #4) — a fire-and-forget
   * refetch is the exact bug FeatureFlagsProvider already had to fix.
   *
   * It resolves with a `tBalanceReadResult` — see that type for why the wider
   * answer exists and who needs it.
   */
  const performFetch = useCallback(
    async (
      targetWorkspaceId: string,
      options?: tBalanceFetchOptions,
    ): Promise<tBalanceReadResult> => {
      const signal = options?.signal;
      issueCounterRef.current += 1;
      const issue = issueCounterRef.current;

      // Keep the current value visible while refetching the SAME workspace.
      // Blanking it on every socket event or post-top-up refetch would flicker
      // the balance, and a consumer that unmounts on a loading flag would hit
      // the RET-1027 trap FeatureFlagsProvider documented. A different
      // workspace goes to `loading`, because showing the old one is finding #1.
      //
      // Returning the SAME state object is also what makes a settlement read
      // invisible while a good reading is on screen: no re-render, so no
      // second mechanism is needed there to suppress this write.
      //
      // Gated on isDiscardedWorkspace: this write runs synchronously with the
      // network call, before any ordering by issue is even possible, so it is
      // the site the RET-1870 stale-`refetchBalance` clobber actually hits —
      // it would otherwise stamp the CURRENT workspace's correct state with
      // this call's now-irrelevant target and never get a chance to recover.
      if (!isDiscardedWorkspace(targetWorkspaceId)) {
        setState((previous) =>
          previous.kind === 'ready' &&
          previous.workspaceId === targetWorkspaceId
            ? previous
            : { kind: 'loading', workspaceId: targetWorkspaceId },
        );
      }

      // RET-2327 (C-1): el instante de EMISIÓN, tomado INMEDIATAMENTE ANTES
      // de llamar al servicio — no al resolver. El orden que importa es el de
      // las lecturas en el SERVIDOR: esta petición se emite ahora, y
      // estampar su llegada dejaría pasar como «más nueva» una lectura
      // emitida antes que una cotización pero resuelta después.
      const readStartedAt = Date.now();

      try {
        const balance = await WalletService.getBalance(targetWorkspaceId);
        if (signal?.cancelled) return DISCARDED;
        // Superseded by a later-issued fetch for the same workspace. Resolving
        // with the balance here would tell an awaiting caller that this value
        // is what the provider is serving, and it is not.
        if (isSuperseded(issue)) return DISCARDED;
        // Same reasoning as the DISCARDED return above: a workspace nobody is
        // looking at anymore is not being served either. Also what lets the
        // floor stay global: a foreign read returns here, before it can raise
        // the bar for the workspace on screen.
        if (isDiscardedWorkspace(targetWorkspaceId)) return DISCARDED;
        lastCommittedIssueRef.current = issue;
        committedBalanceRef.current = {
          workspaceId: targetWorkspaceId,
          amountMicros: balance.amountMicros,
        };
        setState({
          kind: 'ready',
          workspaceId: targetWorkspaceId,
          balance,
          readStartedAt,
        });

        return { kind: 'committed', balance };
      } catch (error) {
        if (signal?.cancelled) return DISCARDED;
        // Same gate as the success path: a stale failure must not blank a
        // balance a newer read already put on screen, because the error card
        // reads as an empty wallet on a workspace that has money.
        if (isSuperseded(issue)) return DISCARDED;
        if (isDiscardedWorkspace(targetWorkspaceId)) return DISCARDED;
        // Deliberately does NOT raise the floor, and the asymmetry is the
        // point. An error is not a reading, so it must not shadow one still in
        // flight: when an older read succeeds after this failure, its number is
        // the only number anyone has, and it gets to land. Raising the floor
        // here turned that into a deterministic loss — before ordering existed,
        // whichever response resolved last won, so the good balance at least
        // had a chance; ordering by issue made the newer failure always win.
        //
        // Fail closed for display: no balance rather than a stale or invented
        // one. `error` is a distinct status, never "no money" — the R2/R3 send
        // gate depends on telling those apart (review finding #5). Recovery is
        // the Retry button (BalanceCard.connected.tsx) or the next
        // workspace.updated refetch. There is no timer, so a surface with no
        // retry affordance waits for the socket.
        //
        // The settlement wait is the one caller that opts out, because inside a
        // 15s wait this rule inverts: a transient 424 on the second tick would
        // replace a good visible balance with the error card and a later tick
        // would bring it back, so the customer watches the number vanish and
        // return (R3). The failure still travels in the return value, so the
        // wait classifies it without this write.
        //
        // The cache dies with the reading it caches: once the error card is
        // what is on screen, a later wait taking that `amountMicros` as its
        // baseline would see a change on its first read and stop one read in —
        // the original RET-1870 symptom, rebuilt. Inside the gate, so the
        // wait's own suppressed failures cannot destroy the baseline they need.
        if (!options?.preserveVisibleBalanceOnFailure) {
          committedBalanceRef.current = null;
          setState({ kind: 'error', workspaceId: targetWorkspaceId, error });
        }

        return { kind: 'failed', error };
      }
    },
    [isSuperseded, isDiscardedWorkspace],
  );

  useEffect(() => {
    // Flags still loading: we do not yet know whether the wallet applies to
    // this workspace. FeatureFlagsProvider deliberately keeps the PREVIOUS
    // workspace's flags visible while it refetches (RET-1027), so acting on
    // `isWalletEnabled` here would fire a request for the new workspace under
    // the old workspace's flag — breaking "zero wallet traffic when unflagged"
    // (review finding #2).
    if (isLoadingWalletFlag) return;

    // No workspace (logout, or the gap between workspaces) and flag off both
    // mean the same thing: nothing to show and nothing to ask for.
    if (!workspaceId || !isWalletEnabled) {
      setState({ kind: 'idle' });
      committedBalanceRef.current = null;

      return;
    }

    const signal = { cancelled: false };
    performFetch(workspaceId, { signal });

    return () => {
      signal.cancelled = true;
    };
  }, [
    workspaceId,
    isWalletEnabled,
    isLoadingWalletFlag,
    refetchTrigger,
    performFetch,
  ]);

  /**
   * El contador de la franja de Meta (RET-2283, docs/loop/RET-2233-estado.md
   * "Rebanada 4"). Es por WORKSPACE (`RE-13`), no por chat — una sola lectura
   * aquí arriba la comparten todos los chats abiertos, con refresco de 60 s
   * MIENTRAS haya al menos un compositor WAPI vivo (`hasOpenWapiComposer`,
   * `useWapiComposerPresence.ts` — el provider no puede leer `selectedChat`
   * por su cuenta, está por encima de `ChatProvider`) Y la cohorte de wallet
   * esté encendida. Fuera de cualquiera de las dos condiciones, CERO lecturas
   * nuevas — ni siquiera una para "apagar" el valor: el gate de abajo lo pone
   * en `idle` sin pedir nada.
   *
   * Pide el modo LIGERO (`lines: false`): el desglose completo del mes no lo
   * necesita nadie aquí y proyecta ~160,000 renglones para el peor workspace
   * (decisión de la Rebanada 1, whatsapp-business-service).
   *
   * Misma disciplina de estampa que el saldo: una respuesta para un workspace
   * que ya no es el de pantalla, o llegada después de que las condiciones de
   * arriba dejaron de cumplirse, nunca se escribe (`cancelled`). Y, como el
   * saldo, una lectura NUEVA para el MISMO workspace conserva la anterior
   * mientras llega (no flashea a "no sé"); cambiar de workspace si fuerza
   * `loading` de inmediato, así que el valor del workspace anterior nunca
   * sobrevive al cambio.
   */
  useEffect(() => {
    if (isLoadingWalletFlag) return;

    // Sin workspace o cohorte apagada: no hay NADA que conservar — el
    // contador de este provider ya no aplica, sea de quien sea.
    if (!workspaceId || !isWalletEnabled) {
      setFreeTierState({ kind: 'idle' });

      return;
    }

    if (!hasOpenWapiComposer) {
      /**
       * RET-2319: dejar de leer no es lo mismo que olvidar. Antes esta
       * rama caía al `setFreeTierState({ kind: 'idle' })` de arriba sin
       * mirar si cambió el workspace, así que cuando el último compositor
       * WAPI se iba un instante —el panel de variables de plantilla
       * desmonta `TextAreaContainer`, D-24 de RET-2232— borraba un
       * contador YA LISTO del MISMO workspace, y el sello volvía a
       * afirmar «Con costo» sobre una franja que seguía abierta.
       *
       * Ahora sólo se limpia lo que pertenece a OTRO workspace (o que
       * nunca llegó a `ready`): un `ready` del workspace EN PANTALLA
       * sobrevive sin compositor abierto. La compuerta sigue sin pedir
       * una lectura nueva mientras no vuelva un compositor — esto no
       * cambia la cadencia de lectura, sólo lo que se sirve mientras no
       * se lee.
       */
      // Deja de leer, pero no olvida — mientras el valor siga FRESCO. Pasada
      // la cota se suelta: un contador viejo servido en el remontaje afirmaría
      // franja sobre un dato muerto (ver FREE_TIER_READY_MAX_AGE_MS).
      setFreeTierState((previous) =>
        esLecturaFresca(previous, workspaceId) ? previous : { kind: 'idle' },
      );

      return;
    }

    let cancelled = false;

    async function readFreeTierCounter() {
      setFreeTierState((previous) =>
        esLecturaFresca(previous, workspaceId)
          ? previous
          : { kind: 'loading', workspaceId },
      );

      try {
        const detail = await WalletService.getFreeMonthDetail(
          workspaceId,
          currentWalletMonthKey(),
          { lines: false },
        );
        if (cancelled) return;
        setFreeTierState({
          kind: 'ready',
          workspaceId,
          counter: detail.tierCounter,
          readAt: Date.now(),
        });
        // La lectura SÍ llegó: borra la marca de fallo para que, si vuelve a
        // caer más tarde, se reporte de nuevo (el flanco, no el estado).
        reportedFreeTierReadFailures.current.delete(workspaceId);
      } catch (error) {
        if (cancelled) return;
        // RET-2319: `failed`, no `idle`, y la distinción SÍ hace falta. Una
        // lectura caída es una incógnita DURABLE —se preguntó y no hubo
        // respuesta— y el sello la lee como «Con costo»; una espera, en
        // cambio, lo hace callar. Mapearlas al mismo estado es lo que hacía
        // que el sello afirmara precio sin datos.
        setFreeTierState({ kind: 'failed', workspaceId });

        // Arreglo C: sólo la PRIMERA falla de ESTE workspace en esta sesión
        // — ver el docblock de `reportedFreeTierReadFailures` más arriba.
        if (!reportedFreeTierReadFailures.current.has(workspaceId)) {
          reportedFreeTierReadFailures.current.add(workspaceId);
          sentry.captureException(error, {
            extra: {
              endpoint: 'workspace/{wid}/wallet/consumption/free',
              workspaceId,
            },
          });
        }
      }
    }

    void readFreeTierCounter();
    const intervalId = setInterval(() => {
      void readFreeTierCounter();
    }, 60000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [workspaceId, isWalletEnabled, isLoadingWalletFlag, hasOpenWapiComposer]);

  /**
   * RET-2319 · el temporizador que cierra el frame que el memo no puede.
   *
   * ESTA pieza es la que hace el trabajo, y el porqué está corregido respecto a
   * lo que este comentario decía antes: la razón original era falsa, y en la
   * dirección que engaña (hallazgo de la revisión arquitectónica). La cota se
   * evalúa también en la derivación del contexto, pero **el memo NO corrige en
   * cualquier re-render**: `Date.now()` no puede ser dependencia de un
   * `useMemo`, así que un re-render con las deps intactas devuelve el valor
   * cacheado, con el contador rancio y `status: 'ready'` incluidos.
   *
   * Lo que cierra el agujero es este temporizador: degrada el `ready` al
   * cumplirse la cota, de modo que cuando el compositor se remonta —y el
   * provider NO se re-renderiza por eso: un hijo que se monta no re-renderiza a
   * su padre— ya no hay estado viejo que leer. Medido por mutación: al borrar
   * este efecto se pone roja `A5′-2`, con el primer frame del remontaje
   * pintando franja sobre el valor rancio.
   *
   * No compite con el poll: con el compositor abierto la relectura refresca
   * `readAt` cada 60 s, así que un temporizador a 120 s nunca llega a disparar.
   * Sólo cuenta cuando ya no hay lecturas, que es justo el caso que importa.
   */
  useEffect(() => {
    if (freeTierState.kind !== 'ready') return;

    const restante =
      FREE_TIER_READY_MAX_AGE_MS - (Date.now() - freeTierState.readAt);

    if (restante <= 0) {
      setFreeTierState({ kind: 'idle' });

      return;
    }

    const estampa = freeTierState.readAt;
    const timeoutId = setTimeout(() => {
      // Se compara la estampa para no pisar una lectura MÁS NUEVA que haya
      // aterrizado mientras este temporizador esperaba.
      setFreeTierState((previous) =>
        previous.kind === 'ready' && previous.readAt === estampa
          ? { kind: 'idle' }
          : previous,
      );
    }, restante);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [freeTierState]);

  const refetchBalance =
    useCallback(async (): Promise<tWalletBalance | null> => {
      if (!workspaceId || !isWalletEnabled) return null;

      const result = await performFetch(workspaceId);

      return result.kind === 'committed' ? result.balance : null;
    }, [workspaceId, isWalletEnabled, performFetch]);

  /**
   * La lectura que `revalidateBalance` tiene viva ahora mismo, estampada con el
   * workspace que la emitió (RET-2107).
   *
   * SÓLO guarda las lecturas que emitió `revalidateBalance`. La del efecto
   * principal queda deliberadamente fuera: ese efecto también corre por
   * `refetchTrigger`, o sea por el socket, y ahí unirse a una lectura emitida
   * ANTES de la señal serviría justo el número que la señal declaró vencido —
   * el defecto de RET-1870 entrando por la puerta de la coalescencia.
   *
   * La estampa es lo que impide que una lectura del workspace anterior
   * satisfaga a una superficie del nuevo. No hace falta limpiarla además al
   * cambiar de workspace: el emisor la limpia cuando su lectura se asienta, así
   * que la entrada no sobrevive a su propia lectura.
   *
   * La promesa guardada NUNCA se rechaza, y por eso se envuelve al emitirla:
   * es COMPARTIDA, así que un rechazo saldría una vez por cada superficie que
   * se unió, y todas llaman con `void`. `performFetch` hoy resuelve sus fallos
   * en vez de rechazarlos, pero la garantía tiene que vivir aquí, donde está el
   * reparto.
   */
  const inFlightRevalidationRef = useRef<{
    workspaceId: string;
    read: Promise<void>;
  } | null>(null);

  /**
   * Asegura que hay una lectura reciente del saldo, sin emitir una petición por
   * llamador (RET-2107).
   *
   * NO es `refetchBalance` con otro nombre, y la diferencia es la GARANTÍA.
   * `refetchBalance` promete «lee ahora», y dos consumidores dependen de esa
   * promesa literal: el botón de reintento de la tarjeta y la espera de
   * acreditación, que compara lecturas sucesivas para detectar que el saldo
   * subió. Darle coalescencia a ese camino uniría dos ticks de la espera a la
   * misma lectura en vuelo, compararía un número contra sí mismo y quemaría un
   * tick — RET-1870 reconstruido desde el otro lado. Por eso son dos entradas y
   * no dos caminos para un trabajo.
   *
   * La coalescencia vive ANTES de `performFetch`, nunca dentro: decide si se
   * emite una lectura, jamás cuál se compromete. El orden por issue y la
   * estampa de workspace siguen gobernando eso, intactos.
   *
   * Sin ventana de frescura ni temporizador: un montaje cinco segundos después
   * sí vuelve a leer. Elegir un N es una decisión de cadencia con costo, y ése
   * es el trabajo de RET-2164, con medición.
   */
  const revalidateBalance = useCallback(async (): Promise<void> => {
    // Mismas guardias de salida que `refetchBalance`, y sin tocar la
    // referencia: la invariante de «cero tráfico de wallet sin bandera» no
    // admite que esta entrada sea la excepción.
    if (!workspaceId || !isWalletEnabled) return;

    const inFlight = inFlightRevalidationRef.current;
    if (inFlight?.workspaceId === workspaceId) {
      await inFlight.read;

      return;
    }

    // NO puede degradar lo que ya está en pantalla, y por eso lleva la misma
    // política de fallo que la espera de acreditación (RET-2107, revisión).
    //
    // Es una lectura de FONDO: los tres montajes llaman con `void` y nadie
    // actúa sobre su resultado. Cambiar una cifra correcta por la tarjeta de
    // error ante un hipo de red es pérdida pura, y pasa en la acción más común
    // —entrar a la pestaña—, que antes de este ticket no emitía ninguna
    // lectura. Peor: la rama de fallo también borra `committedBalanceRef`, que
    // es la línea base de la espera de RET-1870, así que un fallo aquí dentro
    // de la ventana de 15 s desarma la protección de la espera.
    //
    // CONDICIONADO y no `true` a secas: decir que sí sin nada en pantalla
    // suprime la tarjeta de error junto con su botón de Reintentar y deja un
    // spinner sin petición detrás — ver `tBalanceFetchOptions`. El predicado es
    // el mismo que `runSettlementWait` ya usa, no uno nuevo.
    const read = performFetch(workspaceId, {
      preserveVisibleBalanceOnFailure:
        committedBalanceRef.current?.workspaceId === workspaceId,
    }).then(
      () => undefined,
      () => undefined,
    );
    inFlightRevalidationRef.current = { workspaceId, read };

    try {
      await read;
    } finally {
      // En `finally` y no en el camino feliz: una lectura que falla tiene que
      // soltar la referencia igual, o la coalescencia se queda pegada a una
      // promesa muerta y el saldo no se vuelve a leer al montar NUNCA — el bug
      // de vuelta, en silencio y sólo después de un error.
      //
      // Por identidad y no a ciegas, para no borrar la entrada de una lectura
      // posterior que ya ocupó el lugar.
      if (inFlightRevalidationRef.current?.read === read) {
        inFlightRevalidationRef.current = null;
      }
    }
  }, [workspaceId, isWalletEnabled, performFetch]);

  /**
   * Bounded settlement wait after an accepted top-up (RET-1870, R1-R6).
   *
   * A 200 on the charge means charged, NOT credited: the ledger row arrives on
   * Stripe's webhook a moment later. The single read fired at the 200 races
   * that webhook and usually loses, and nothing read again — the customer kept
   * seeing the pre-credit number until they navigated away and back. This
   * re-reads with increasing gaps and stops the moment the balance changes.
   *
   * The generation counter is the only cancellation primitive, and it covers
   * every death a wait can have: a second charge supersedes it (R6), and a
   * workspace change, the wallet flag going off, or an unmount kills it (R5).
   * No `AbortController` — the file already chose the flag object.
   */
  const settlementGenerationRef = useRef(0);
  const settlementSleepRef = useRef<{
    timeoutId: ReturnType<typeof setTimeout>;
    release: () => void;
  } | null>(null);

  /**
   * Ends the gap between two reads. Clearing the timer alone would leave the
   * loop suspended on a promise that never settles, so the resolver is kept
   * next to the timer id and called here: the loop resumes, sees its
   * generation is gone and returns. No timer outlives the wait, and nothing is
   * written after it (R5).
   */
  const cancelSettlementSleep = useCallback(() => {
    const sleeping = settlementSleepRef.current;
    if (!sleeping) return;

    settlementSleepRef.current = null;
    clearTimeout(sleeping.timeoutId);
    sleeping.release();
  }, []);

  const sleepBeforeNextRead = useCallback(
    (delayMs: number): Promise<void> =>
      new Promise((resolve) => {
        const timeoutId = setTimeout(() => {
          settlementSleepRef.current = null;
          resolve();
        }, delayMs);

        settlementSleepRef.current = { timeoutId, release: resolve };
      }),
    [],
  );

  const runSettlementWait = useCallback(
    async (targetWorkspaceId: string, generation: number): Promise<void> => {
      const isCurrentWait = () =>
        settlementGenerationRef.current === generation;
      // The shape `performFetch` already takes, backed by the generation
      // instead of by a flag of its own: a live view of it, not a fourth piece
      // of state that could disagree. Bumping the generation therefore also
      // cancels the read in flight, so a dead wait commits nothing.
      //
      // It is ALSO what rejects the writes of a read still in flight when the
      // workspace changed, so no workspace clause belongs here: the
      // cancellation effect is keyed on `workspaceId`, so a switch bumps the
      // generation before any response can land. One was tried and no test
      // could kill it. What the generation cannot see is a wait that should
      // never have STARTED — that is the guard in `settleBalanceAfterCharge`.
      const signal = {
        get cancelled() {
          return !isCurrentWait();
        },
      };
      const committedAmountMicros = (): string | null =>
        committedBalanceRef.current?.workspaceId === targetWorkspaceId
          ? committedBalanceRef.current.amountMicros
          : null;

      // Captured before the first await, and never re-read from state (KTD3).
      let baseline = committedAmountMicros();

      for (let read = 0; ; read += 1) {
        // Recomputed per read, not captured once: a read that fails after the
        // state already went to `error` has nothing left to protect, and must
        // let the error card through. See `tBalanceFetchOptions` for the rule.
        const result = await performFetch(targetWorkspaceId, {
          signal,
          preserveVisibleBalanceOnFailure: committedAmountMicros() !== null,
        });
        if (!isCurrentWait()) return;

        // R4: a terminal failure spends none of the remaining ticks. Nothing
        // retried inside 15s fixes an invalid workspace, an expired session, a
        // workspace this user is not a member of, or a broken money contract.
        // The classification lives in `Wallet.settlement.ts` and is not
        // re-derived here. Whether it also commits the error is not decided
        // here either — the rule above already decided it, from what the
        // customer can see.
        if (
          result.kind === 'failed' &&
          classifySettlementFailure(result.error) === 'terminal'
        ) {
          // El candado SOBREVIVE al fallo terminal (RET-1925, corrección de
          // revisión). Aquí se escribía `idle`, con el argumento de que "el
          // estado de error del saldo ya cuenta esa otra historia". Ese
          // argumento es FALSO y medirlo fue el hallazgo: esta lectura corre con
          // `preserveVisibleBalanceOnFailure` en true mientras haya saldo en
          // pantalla, así que el error NO se escribe — el status se queda en
          // `ready` con la cifra vieja. La pantalla vuelve a verse exactamente
          // como antes de la recarga, y desarmar el candado ahí deja pasar un
          // segundo cobro real: el defecto que este ticket cierra, entrando por
          // la puerta del fallo terminal.
          //
          // El cobro sí ocurrió; lo que falló es leerlo. Se marca agotado, que
          // es lo que el candado necesita, y la espera no gasta más lecturas
          // porque nada retriable arregla un 403 ni un contrato roto.
          setSettlement((previous) =>
            previous.kind === 'settling'
              ? {
                  kind: 'exhausted',
                  workspaceId: targetWorkspaceId,
                  amountMicros: previous.amountMicros,
                }
              : previous,
          );

          return;
        }

        // A discarded read is not a loss. It means a newer read committed
        // first — the socket refetch, typically — so what is on screen may
        // already be the credited value. Ask what committed, instead of
        // spending ticks against our own ordering guard.
        const reading =
          result.kind === 'committed'
            ? result.balance.amountMicros
            : committedAmountMicros();

        if (reading !== null) {
          if (baseline === null) {
            // Nothing was on screen when the charge landed: the first load
            // failed, or was still in flight. The first reading becomes the
            // baseline, because a change can only be seen against something.
            // Worst case the wait spends its ticks on an already credited
            // balance, which costs requests and shows nothing wrong.
            baseline = reading;
          } else if (hasSettlementBalanceIncreased(baseline, reading)) {
            // La acreditación llegó. Volver a reposo es lo que hace que la
            // línea desaparezca y la cifra nueva se pinte sola: la
            // confirmación del criterio 3 es el cambio de estado, no un aviso
            // aparte que podría discrepar de lo que la tarjeta muestra.
            setSettlement({ kind: 'idle' });

            return;
          }
        }

        // R2: the budget is spent and nothing was credited. The last real value
        // stays on screen — no error, and no number the API never returned.
        if (read >= SETTLEMENT_WAIT_SCHEDULE_MS.length) {
          // Y ahora la pantalla lo DICE (RET-1925, criterio 4). Antes esta
          // rama era un `return` mudo: se agotaban los 15 s, el saldo viejo se
          // quedaba en pantalla sin error y sin aviso, y eso se veía igual que
          // no haber recargado nunca. Se guarda el saldo contra el que se
          // rindió para que el estado se apague solo si el dinero aparece
          // después por el socket o por un reintento.
          setSettlement((previous) =>
            previous.kind === 'idle'
              ? previous
              : {
                  kind: 'exhausted',
                  workspaceId: targetWorkspaceId,
                  amountMicros: previous.amountMicros,
                },
          );

          return;
        }

        await sleepBeforeNextRead(SETTLEMENT_WAIT_SCHEDULE_MS[read]);
        if (!isCurrentWait()) return;
      }
    },
    [performFetch, sleepBeforeNextRead],
  );

  /**
   * R7: only a real charge starts a wait, and only for the workspace on screen.
   *
   * An unrecognized status starts nothing and says so out loud. `status` is a
   * bare string on the wire, so "the backend renamed `charged`" and "this
   * customer's credit landed before the first read" are observationally
   * identical from here: silence would let the feature be dead for every
   * customer with no signal at all. Verified: wallet-service's status set is
   * closed (`charged | declined | pending`) and a `declined` arrives as a 402
   * that throws before this runs, so failing closed is safe today and the gap
   * is observability, not correctness. The status carries no PII. `pending` is
   * expected, so it is not reported.
   */
  const settleBalanceAfterCharge = useCallback(
    (outcome: tWalletChargeOutcome, chargedAmountMicros?: string): void => {
      const isCharged = outcome.status === CHARGED_STATUS;

      if (!isCharged && outcome.status !== PENDING_STATUS) {
        sentry.captureMessage('wallet top-up: unrecognized charge status', {
          level: 'warning',
          extra: { status: outcome.status },
        });
      }

      if (!workspaceId || !isWalletEnabled) return;

      // `submit` awaits the charge POST — as long as Stripe takes — and only
      // then calls the callback it captured at click time, so a sidebar switch
      // during that POST fires it bound to the workspace the customer left. Its
      // reads would stamp the shared state with that workspace, and the memo's
      // stamp filter hides the wrong number but cannot un-clobber the stamp:
      // the workspace actually on screen falls from its known-good balance to
      // `loading`.
      if (workspaceId !== currentWorkspaceIdRef.current) return;

      // One read for anything that is not a charge. Closing the modal used to
      // re-read unconditionally, so a 3DS `pending` that resolved quickly got
      // exactly this read; RET-1870 moved the re-read to the charge and left
      // `pending` with zero. It costs the same one request as the behaviour it
      // restores, and the wait stays reserved for a credit that is coming.
      if (!isCharged) {
        void refetchBalance();

        return;
      }

      // Bump BEFORE clearing the previous timer (R6). The generation is what a
      // running wait checks, so invalidating first leaves no window where the
      // old wait wakes up still believing it is the current one — the
      // session-counter rule from
      // `docs/solutions/patterns/session-based-event-invalidation.md`.
      settlementGenerationRef.current += 1;
      const generation = settlementGenerationRef.current;
      cancelSettlementSleep();

      // Aquí y no antes (RET-1925): un 3DS `pending` recibe una relectura y
      // ninguna espera, así que narrar "acreditándose" ahí prometería una
      // acreditación que nadie está mirando. Y un cobro que llegó atado al
      // workspace que el cliente ya dejó ni siquiera llega a esta línea.
      //
      // El segundo cobro dentro de la ventana cae aquí también, y por eso
      // reescribe el monto en vez de sumarse: hay UNA espera viva, la que la
      // generación acaba de declarar, y el estado tiene que hablar de ésa.
      setSettlement({
        kind: 'settling',
        workspaceId,
        amountMicros: chargedAmountMicros ?? null,
      });

      void runSettlementWait(workspaceId, generation);
    },
    [
      workspaceId,
      isWalletEnabled,
      refetchBalance,
      cancelSettlementSleep,
      runSettlementWait,
    ],
  );

  /**
   * Aplica un desenlace del modal (RET-1928): consulta la tabla y dispara lo
   * que ésta declare, y nada más.
   *
   * Es la única traducción desenlace -> qué releer. Antes esa regla vivía
   * repartida en lo que cada autor recordó, y por eso el cierre del modal
   * releía el SALDO en las dos pantallas que lo montaban aunque lo que hubiera
   * cambiado fuera la configuración de la recarga.
   *
   * No decide nada por su cuenta: `settleBalanceAfterCharge` conserva intactas
   * su firma y sus reglas —qué status arranca la espera, cuál recibe una sola
   * relectura, cuál se reporta— y aquí sólo cambia QUIÉN lo llama. El aviso no
   * se dispara desde el provider: la tabla dice si lo hay, y lo pinta el hook
   * que envuelve esto, porque un provider de estado no es el lugar de la UI.
   */
  const applyOutcome = useCallback(
    (outcome: tTopUpOutcome): void => {
      const effects = effectsFor(outcome);

      // Una sola condición, y es la de la tabla. Preguntar aquí otra vez por el
      // kind serían dos condiciones decidiendo lo mismo, y un kind futuro con
      // cobro que asentar se habría ignorado en silencio pese a tener su fila.
      if (effects.settleCharge) {
        // El monto va aquí y no dentro de `tWalletChargeOutcome` porque esa
        // forma es la vista estrecha que la espera lee del CABLE, y el cable no
        // devuelve el monto. Ensancharla habría obligado al `tTopUpCharge` del
        // modal a crecer un campo que su respuesta no tiene.
        settleBalanceAfterCharge(
          effects.settleCharge.charge,
          effects.settleCharge.amountMicros,
        );
      }

      if (effects.invalidatesAutoTopUp) {
        setAutoTopUpToken((previous) => previous + 1);
      }

      // RET-2427: el veredicto de saldo bajo viaja con el saldo, y este
      // desenlace cambió si algo lo va a rellenar. Una lectura que falla deja
      // el saldo que ya estaba: el aviso puede esperar al siguiente evento, y
      // cambiar un saldo bueno por la tarjeta de error sería peor. Sólo si HAY
      // uno en pantalla — misma guardia que la espera de liquidación: sin él,
      // la lectura ya escribió `loading`, y callar su fallo dejaría la tarjeta
      // en el esqueleto sin el botón de reintentar.
      if (effects.rereadsBalance && workspaceId && isWalletEnabled) {
        void performFetch(workspaceId, {
          preserveVisibleBalanceOnFailure:
            committedBalanceRef.current?.workspaceId === workspaceId,
        });
      }
    },
    [settleBalanceAfterCharge, workspaceId, isWalletEnabled, performFetch],
  );

  /**
   * R5, three paths out of one cleanup: React runs it when `workspaceId`
   * changes, when `isWalletEnabled` changes, and again on unmount. The wait
   * dies with the workspace it belonged to, and the stamp still filters any
   * response that lands afterwards.
   *
   * `isWalletEnabled` is in here because the flag can go off IN PLACE, without
   * a workspace change: the main effect then writes `idle` and clears the
   * cache, while a wait that knew nothing about it kept calling the wallet
   * endpoint and re-populating what was just cleared — against this file's own
   * "zero wallet traffic when unflagged" invariant.
   *
   * Deliberately NOT here: `isLoadingWalletFlag`, which flips on every flag
   * refetch and would kill live waits for no reason, and `refetchTrigger`,
   * which is `workspace.updated` — a billing event is not a reason to abandon
   * the wait it may itself be about (pinned by its own test).
   */
  useEffect(
    () => () => {
      settlementGenerationRef.current += 1;
      cancelSettlementSleep();
      // El relato muere con la espera (RET-1925). La estampa del memo ya
      // esconde el estado de otro workspace, pero esconder no es borrar: sin
      // esta línea, salir de A hacia B y volver a A repinta un `settling`
      // estampado con A cuya espera ya está muerta, y la línea se queda para
      // siempre porque nadie va a llegar a la transición que la apaga.
      setSettlement({ kind: 'idle' });
    },
    [workspaceId, isWalletEnabled, cancelSettlementSleep],
  );

  const value = useMemo(() => {
    // Only state stamped with the workspace on screen is allowed through.
    const scoped =
      state.kind !== 'idle' && state.workspaceId === workspaceId ? state : null;

    const status: tWalletStatus = isLoadingWalletFlag
      ? 'unknown'
      : !workspaceId || !isWalletEnabled
        ? 'disabled'
        : scoped?.kind === 'ready'
          ? 'ready'
          : scoped?.kind === 'error'
            ? 'error'
            : 'loading';

    const balance = scoped?.kind === 'ready' ? scoped.balance : null;
    // RET-2327 (C-1): misma estampa que `balance` — sólo un `ready` del
    // workspace EN PANTALLA sirve su marca; cualquier otro caso (idle,
    // loading, error, otro workspace) cae a `null`.
    const balanceReadStartedAt =
      scoped?.kind === 'ready' ? scoped.readStartedAt : null;

    /**
     * RET-2319 · el contexto expone un DISCRIMINANTE de tres salidas, no un
     * booleano al lado del contador, porque el sello necesita distinguir tres
     * cosas y un booleano sólo parte dos:
     *
     *   `ready`    hay contador fresco del workspace en pantalla ⇒ decide.
     *   `waiting`  la respuesta viene, o el valor que hay es de otro workspace
     *              o está rancio ⇒ el sello CALLA.
     *   `unknown`  se preguntó y no hubo respuesta, o estamos fuera del
     *              provider ⇒ el sello dice «Con costo».
     *
     * LA VEJEZ SE JUZGA TAMBIÉN AQUÍ, y hay que ser exacto sobre para qué
     * sirve, porque la versión anterior de este comentario mentía. NO es que
     * «cualquier re-render corrija»: `Date.now()` no puede ser dependencia de un
     * `useMemo`, así que con las deps intactas esto devuelve el valor cacheado,
     * rancio incluido. Quien cierra el caso normal es el temporizador de arriba.
     *
     * Esto es el cinturón para el caso que el temporizador NO cubre: una
     * pestaña en segundo plano, donde el navegador estrangula o aplaza
     * `setTimeout`. Al volver, cualquier re-render que sí cambie una dep
     * recalcula y atrapa el `ready` vencido en vez de servirlo.
     *
     * NO CUBRE EL FRAME DEL REMONTAJE, y por eso no es lo único que hay. Con el
     * temporizador sin disparar y el compositor remontándose, ese commit no
     * cambia NINGUNA dep de este memo — `hasOpenWapiComposer` no está entre
     * ellas, y meterlo no ayudaría: la presencia se registra en un `useEffect`
     * PASIVO (`useWapiComposerPresence.ts:57`), así que la invalidación llega
     * DESPUÉS del commit. Ese render serviría el `ready` vencido.
     *
     * Lo que cierra ese frame es el CONSUMIDOR: el sello recibe
     * `freeTierCounterReadAt` y vuelve a juzgar la vejez con el reloj de su
     * propio render, que es el único que ve el salto. Ver
     * `hooks/useComposerServiceSeal.ts` (`freeTierReadingIsStale`) y
     * `Wallet.freeTier.ts`, dueño único de la cota para que las dos lecturas no
     * puedan separarse. El caso está medido por
     * `ret2319_Wallet.provider.freeTier.v3.test.tsx` (S-1): el reloj avanza con
     * `vi.setSystemTime` SIN avanzar timers —una máquina suspendida, donde el
     * reloj monótono que cuenta el `setTimeout` se pausa mientras el de pared
     * salta— y el sello calla desde el índice 0 del diario.
     *
     * Y ESTE CINTURÓN YA TIENE VARA, lo que antes no era cierto: S-2 del mismo
     * archivo lo mide (reloj vencido + un re-render que sí mueve una dep ⇒ el
     * contexto deja de servir el contador), y S-3 fija el filo de la cota sobre
     * el módulo puro. Quitarle el término de edad al predicado ya no deja la
     * suite en verde.
     */
    const freeTierReadyParaPantalla = esLecturaFresca(
      freeTierState,
      workspaceId,
    );

    const freeTierCounter = freeTierReadyParaPantalla
      ? freeTierState.counter
      : null;

    /*
     * RET-2319 · vuelta 3 · la estampa viaja CON el contador que describe, y por
     * eso se sirve desde la misma rama: el invariante
     * `readAt !== null` ⟺ `freeTierCounter !== null` ⟺ `status === 'ready'`
     * es lo que hace observable el orden del ternario del sello.
     *
     * Sirve para lo que este archivo NO puede hacer. Cuando el compositor se
     * remonta sobre una pestaña que durmió —el reloj de pared saltó pero el
     * temporizador no llegó a disparar—, ESTE memo devuelve el valor cacheado:
     * ninguna de sus deps cambia en ese commit. Con la estampa en la mano, el
     * sello vuelve a juzgar la vejez en su propio render, que sí ve el reloj
     * nuevo, y calla en vez de prometer franja sobre un contador muerto.
     */
    const freeTierCounterReadAt = freeTierReadyParaPantalla
      ? freeTierState.readAt
      : null;

    const freeTierCounterStatus: tFreeTierCounterStatus =
      freeTierReadyParaPantalla
        ? 'ready'
        : freeTierState.kind === 'failed' &&
            freeTierState.workspaceId === workspaceId
          ? 'unknown'
          : 'waiting';

    // RET-2319: separa «cargando» (transitorio, la respuesta viene) de todo
    // lo demás que ya caía en el mismo `null` de arriba (durable: la lectura
    // cayó, la cohorte está apagada, o es de OTRO workspace). SÓLO `true`
    // para un `loading` estampado con el workspace EN PANTALLA — un refresco
    // que conserva un `ready` del MISMO workspace (ver el `setFreeTierState`
    // de arriba) nunca entra aquí, así que esto no se enciende de nuevo
    // mientras ya hay un contador servido.
    // OBSOLETO (RET-2319): lo reemplaza `freeTierCounterStatus`. Sin lectores
    // en producción; sigue aquí sólo porque dos varas congeladas arman
    // `tWalletContext` a mano y lo sirven, así que se retira EN EL MISMO cambio
    // que migre esos dos arneses.
    //
    // Conserva su definición LITERAL de la vuelta 1 y NO se deriva del
    // discriminante, que es como lo había dejado: `status === 'waiting'` cubre
    // además «nunca se preguntó», «otro workspace» y «ready rancio», así que
    // derivarlo le ensanchaba el significado EN SILENCIO a un campo que ningún
    // aserto fija (hallazgo de la revisión arquitectónica). Un campo deprecado
    // no debe cambiar de semántica: quien todavía lo lea sigue leyendo lo mismo.
    const isLoadingFreeTierCounter =
      freeTierState.kind === 'loading' &&
      freeTierState.workspaceId === workspaceId;

    return {
      status,
      balance,
      balanceError: scoped?.kind === 'error' ? scoped.error : null,
      isLoadingBalance: status === 'loading',
      refetchBalance,
      revalidateBalance,
      settleBalanceAfterCharge,
      applyOutcome,
      autoTopUpToken,
      settlement: settlementForScreen(settlement, workspaceId),
      freeTierCounter,
      balanceReadStartedAt,
      freeTierCounterReadAt,
      freeTierCounterStatus,
      isLoadingFreeTierCounter,
    };
  }, [
    state,
    workspaceId,
    isWalletEnabled,
    isLoadingWalletFlag,
    refetchBalance,
    revalidateBalance,
    settleBalanceAfterCharge,
    applyOutcome,
    autoTopUpToken,
    settlement,
    freeTierState,
  ]);

  return (
    <WalletContext.Provider value={value}>
      {props.children}
    </WalletContext.Provider>
  );
}
